/**
 * 要確認（FR-25/FR-30・07 §6.2・design-brief-ai-inbox §5）＝AI が確信を持てなかった箇所を、
 * その場で人が解消する。**すべて解消するまで承認できない**（can_approve はサーバが配る＝FR-30）。
 *
 * 候補ボタン＝AI が挙げた候補（`flag.candidates`）をワンタップで確定。候補に無ければ検索 or
 * マスタ新規登録（現場・得意先＝DA-3）。解消は「値の反映」と「フラグ解消」が同じ操作（片方だけ起きない）。
 *
 * ★候補の `ref` は**表示番号（display_no）**であって内部 id ではない（07 §6.2 の候補は名称・表示番号
 *   だけを載せる＝原文が LLM の外に出ない設計）。resolve は内部 id を要求するので、候補クリックは
 *   **ref で検索 → display_no 一致を内部 id に解決**してから送る（customer/product 共通）。
 */

import { Badge, Card, ErrorNote } from "@kenzai/ui";
import {
  console_,
  type OrderDetail as Detail,
  type Paged,
  type ReviewFlag,
} from "@kenzai/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";

// candidates は backend で単一オブジェクト（`{"type":...,"options":...}` ほか）。型だけ与えて安全に読む。
type Candidates = {
  type?: string;
  options?: unknown[];
  text?: string;
  preview?: unknown;
  source?: string;
} | null;

type EntityCandidate = { ref?: string | null; label?: string; conf?: number };

function candidatesOf(flag: ReviewFlag): Candidates {
  return (flag.candidates as Candidates) ?? null;
}

/** 明細フラグ（`item:{n}:product|qty|price`）なら line_no を返す（ヘッダフラグは null）。 */
export function itemFlagLineNo(field: string): number | null {
  const m = field.match(/^item:(\d+):/);
  return m ? Number(m[1]) : null;
}

/** 未解消フラグのうち、この明細（line_no）に対するもの＝Draft 側の要確認ハイライトが読む。 */
export function itemHasOpenFlag(detail: Detail, lineNo: number): boolean {
  return detail.flags.some((f) => f.resolved_at === null && itemFlagLineNo(f.field) === lineNo);
}

/** ヘッダのこのフィールドに未解消フラグがあるか（Draft のヘッダ確度チップが読む）。 */
export function headerHasOpenFlag(detail: Detail, field: string): boolean {
  return detail.flags.some((f) => f.resolved_at === null && f.field === field);
}

/** AI が読めなかった注文か（07 §7 フォールバック）＝general の ai_failed フラグが未解消で残る。 */
export function isAiFailed(detail: Detail): boolean {
  return detail.flags.some(
    (f) => f.resolved_at === null && f.field === "general" && candidatesOf(f)?.type === "ai_failed",
  );
}

// ---------------------------------------------------------------------------
// 一覧（要確認カード）
// ---------------------------------------------------------------------------

export function Flags({ detail, orderId, isAdmin }: { detail: Detail; orderId: number; isAdmin: boolean }) {
  const openFlags = detail.flags.filter((f) => f.resolved_at === null);
  return (
    <Card style={{ borderColor: "var(--kz-status-warn)", borderWidth: 2 }}>
      <h2 style={{ margin: "0 0 var(--kz-space-3)", fontSize: "var(--kz-font-size-md)", color: "var(--kz-status-warn)" }}>
        要確認（{openFlags.length}）
      </h2>
      <p style={{ margin: "0 0 var(--kz-space-3)", fontSize: 11, color: "var(--kz-color-ink-muted)" }}>
        AI が自信を持てなかった箇所です。確定すると承認できます。
      </p>
      <div style={{ display: "grid", gap: "var(--kz-space-4)" }}>
        {openFlags.map((f) => (
          <FlagRow key={f.id} flag={f} detail={detail} orderId={orderId} isAdmin={isAdmin} />
        ))}
      </div>
    </Card>
  );
}

/** フラグ1件を種類で振り分ける（明細フラグは field の item:{n}:kind、ヘッダは field 名で）。 */
function FlagRow({
  flag,
  detail,
  orderId,
  isAdmin,
}: {
  flag: ReviewFlag;
  detail: Detail;
  orderId: number;
  isAdmin: boolean;
}) {
  const customerId = detail.order.customer_id;
  const line = itemFlagLineNo(flag.field);
  if (line !== null) {
    const kind = flag.field.split(":")[2];
    if (kind === "product") return <ProductFlag flag={flag} orderId={orderId} customerId={customerId} />;
    if (kind === "qty") return <QtyFlag flag={flag} orderId={orderId} customerId={customerId} />;
    if (kind === "price") return <PriceFlag flag={flag} orderId={orderId} customerId={customerId} />;
    return <NoteFlag flag={flag} orderId={orderId} customerId={customerId} />;
  }
  if (flag.field === "customer")
    return <CustomerFlag flag={flag} orderId={orderId} customerId={customerId} isAdmin={isAdmin} />;
  if (flag.field === "site") return <SiteFlag flag={flag} detail={detail} orderId={orderId} />;
  if (flag.field === "date") return <DateFlag flag={flag} detail={detail} orderId={orderId} />;
  if (flag.field === "bin") return <BinFlag flag={flag} orderId={orderId} customerId={customerId} />;
  return <NoteFlag flag={flag} orderId={orderId} customerId={customerId} />;
}

// ---------------------------------------------------------------------------
// 共通の骨組み・スタイル
// ---------------------------------------------------------------------------

function FlagShell({ flag, children }: { flag: ReviewFlag; children: ReactNode }) {
  return (
    <div data-testid={`flag-${flag.id}`}>
      <p style={{ margin: "0 0 var(--kz-space-2)", fontSize: "var(--kz-font-size-sm)", fontWeight: 600 }}>
        {flag.reason}
      </p>
      {children}
    </div>
  );
}

/** フラグ解消の共通 mutation＝解消したら注文・受注管理の一覧（・現場マスタ）を取り直す。 */
function useResolveFlag(orderId: number, flagId: number, customerId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => console_.resolveFlag(orderId, flagId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["order", orderId] });
      // D-76：一覧の queryKey は ["orders", ...]（旧 ["inbox"] は誰にも当たらない＝
      // 要確認を解消しても一覧のバッジが最大30秒古いままになる）
      qc.invalidateQueries({ queryKey: ["orders"] });
      if (customerId != null) qc.invalidateQueries({ queryKey: ["sites", customerId] });
    },
  });
}

/*
 * ★ボタン・チップの見た目は**共有クラス**に寄せた（M1.7・S3）＝この画面だけの
 *   `resolveBtn` / `ghost` / `chip`（いずれも 44px）が、`formStyles.ts` の 44/36px や
 *   `.kz-btn`（40px）と混ざって高さがバラバラだった。
 *   タップ領域 44px（FR-120・05 §5.3）は `mobile.css` がスマホ幅でまとめて確保する。
 *   ・確定＝`kz-btn kz-btn--primary`（この場の主操作）
 *   ・検索＝`kz-btn`
 *   ・候補＝`kz-chip`（AI の候補を1タップで採る＝押せる印の丸いチップ）
 */

const textInput = {
  flex: 1,
  // 390px で input と確定ボタンを1行に並べると input が潰れる＝**最低幅**を決めて折り返させる
  //（親側は `flexWrap` が効いている）。
  minWidth: 180,
  minHeight: 44,
  font: "inherit",
  padding: "var(--kz-space-2)",
  border: "1px solid var(--kz-color-border)",
  borderRadius: "var(--kz-radius-sm)",
} as const;

// ---------------------------------------------------------------------------
// 得意先・商品＝検索して内部 id に解決する共通リゾルバ（候補 ref → 検索 → 一致を採用）
// ---------------------------------------------------------------------------

type SearchRow = { id: number; display_no: string | null; name: string; spec?: string | null };

function EntitySearchResolver({
  flag,
  orderId,
  customerId,
  candidates,
  searchFn,
  toBody,
  rowLabel,
  searchPlaceholder,
  create,
}: {
  flag: ReviewFlag;
  orderId: number;
  customerId: number | null;
  candidates: EntityCandidate[];
  searchFn: (q: string) => Promise<Paged<SearchRow>>;
  toBody: (id: number) => Record<string, unknown>;
  rowLabel: (row: SearchRow) => string;
  searchPlaceholder: string;
  create?: { buttonLabel: string; placeholder: string; buildBody: (name: string) => Record<string, unknown>; blockedHint?: string };
}) {
  const resolve = useResolveFlag(orderId, flag.id, customerId);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchRow[] | null>(null);
  const [mode, setMode] = useState<"pick" | "create">("pick");
  const [newName, setNewName] = useState("");

  // 候補クリック＝ref で検索し display_no 一致があればワンタップ確定、無ければ結果を出して人が選ぶ
  const search = useMutation({
    mutationFn: async (v: { q: string; ref: string | null }) => ({ page: await searchFn(v.q), ref: v.ref }),
    onSuccess: ({ page, ref }) => {
      const exact = ref ? page.items.find((r) => r.display_no === ref) : undefined;
      if (exact) {
        resolve.mutate(toBody(exact.id));
        return;
      }
      setResults(page.items);
    },
  });

  const busy = resolve.isPending || search.isPending;

  return (
    <FlagShell flag={flag}>
      <ErrorNote error={resolve.error ?? search.error} />

      {/* 切り替えは**セグメントタブ**（M1.7・S3）＝選択が枠の色ではなく面で読める */}
      {create && (
        <div style={{ display: "flex", marginBottom: "var(--kz-space-2)" }}>
          <div className="kz-tabs">
            {(["pick", "create"] as const).map((m) => (
              <button
                key={m}
                type="button"
                data-testid={`flag-${flag.id}-mode-${m}`}
                aria-pressed={mode === m}
                onClick={() => setMode(m)}
                className={mode === m ? "kz-tab is-selected" : "kz-tab"}
              >
                {m === "pick" ? "候補・検索から" : "新しく登録"}
              </button>
            ))}
          </div>
        </div>
      )}

      {mode === "create" && create ? (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--kz-space-2)" }}>
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            data-testid={`flag-${flag.id}-new-name`}
            placeholder={create.placeholder}
            style={textInput}
          />
          <button
            type="button"
            data-testid={`flag-${flag.id}-resolve`}
            disabled={newName.trim().length === 0 || busy}
            onClick={() => resolve.mutate(create.buildBody(newName.trim()))}
            className="kz-btn kz-btn--primary"
          >
            {create.buttonLabel}
          </button>
        </div>
      ) : (
        <>
          {candidates.length > 0 && (
            <div style={{ display: "flex", gap: "var(--kz-space-2)", flexWrap: "wrap", marginBottom: "var(--kz-space-2)" }}>
              {candidates.map((c, i) => (
                <button
                  key={i}
                  type="button"
                  data-testid={`flag-${flag.id}-candidate-${i}`}
                  disabled={busy}
                  onClick={() => search.mutate({ q: c.ref ?? c.label ?? "", ref: c.ref ?? null })}
                  className="kz-chip"
                >
                  {c.label}
                  {c.ref ? ` (${c.ref})` : ""}
                  {typeof c.conf === "number" ? ` · ${Math.round(c.conf * 100)}%` : ""}
                </button>
              ))}
            </div>
          )}

          <form
            onSubmit={(e) => {
              e.preventDefault();
              search.mutate({ q: query.trim(), ref: null });
            }}
            style={{ display: "flex", flexWrap: "wrap", gap: "var(--kz-space-2)" }}
          >
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid={`flag-${flag.id}-search`}
              placeholder={searchPlaceholder}
              style={textInput}
            />
            <button type="submit" disabled={busy} className="kz-btn">
              検索
            </button>
          </form>

          {results !== null && (
            <div style={{ marginTop: "var(--kz-space-2)", display: "grid", gap: "var(--kz-space-1)" }}>
              {results.length === 0 ? (
                <span style={{ fontSize: "var(--kz-font-size-sm)", color: "var(--kz-color-ink-muted)" }}>
                  該当がありません。{create ? "「新しく登録」から追加できます。" : "検索語を変えてお試しください。"}
                </span>
              ) : (
                results.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    data-testid={`flag-${flag.id}-result-${r.id}`}
                    disabled={busy}
                    onClick={() => resolve.mutate(toBody(r.id))}
                    className="kz-btn"
                    style={{ justifyContent: "flex-start", textAlign: "left" }}
                  >
                    {rowLabel(r)}
                    {r.display_no ? ` (${r.display_no})` : ""}
                  </button>
                ))
              )}
            </div>
          )}
        </>
      )}
    </FlagShell>
  );
}

function CustomerFlag({
  flag,
  orderId,
  customerId,
  isAdmin,
}: {
  flag: ReviewFlag;
  orderId: number;
  customerId: number | null;
  isAdmin: boolean;
}) {
  const candidates = (candidatesOf(flag)?.options ?? []) as EntityCandidate[];
  return (
    <EntitySearchResolver
      flag={flag}
      orderId={orderId}
      customerId={customerId}
      candidates={candidates}
      searchFn={(q) => console_.customers(q ? `?q=${encodeURIComponent(q)}` : "")}
      toBody={(id) => ({ customer_id: id })}
      rowLabel={(r) => r.name}
      searchPlaceholder="得意先名・番号で検索"
      // 得意先の新規登録は管理者のみ（03 §7）＝スタッフには「新しく登録」を出さない
      create={
        isAdmin
          ? {
              buttonLabel: "登録して確定",
              placeholder: "得意先名（マスタに登録されます）",
              buildBody: (name) => ({ new_customer: { name } }),
            }
          : undefined
      }
    />
  );
}

function ProductFlag({
  flag,
  orderId,
  customerId,
}: {
  flag: ReviewFlag;
  orderId: number;
  customerId: number | null;
}) {
  const candidates = (candidatesOf(flag)?.options ?? []) as EntityCandidate[];
  return (
    <EntitySearchResolver
      flag={flag}
      orderId={orderId}
      customerId={customerId}
      candidates={candidates}
      searchFn={(q) => console_.products(q ? `?q=${encodeURIComponent(q)}` : "")}
      toBody={(id) => ({ product_id: id })}
      rowLabel={(r) => (r.spec ? `${r.name} ${r.spec}` : r.name)}
      searchPlaceholder="品名・規格・番号で検索"
    />
  );
}

// ---------------------------------------------------------------------------
// 現場（既存から選ぶ／新しい現場を登録）＝第1区間からの唯一のフラグ源。testid を維持する。
// ---------------------------------------------------------------------------

/** フラグ理由「…（若葉町 3丁目 新築）。現場マスタと…」から現場名を取り出す（登録の初期値）。
 *  現場名自体に全角括弧が入りうる（例 "田中邸（西棟）"）ので、末尾の「）。現場マスタ」までを貪欲に取る。 */
function extractOtherSiteName(reason: string): string {
  return reason.match(/（(.+)）。現場マスタ/)?.[1] ?? reason.match(/（(.+?)）/)?.[1] ?? "";
}

function SiteFlag({ flag, detail, orderId }: { flag: ReviewFlag; detail: Detail; orderId: number }) {
  const customerId = detail.order.customer_id;
  const resolve = useResolveFlag(orderId, flag.id, customerId);
  const [value, setValue] = useState<string>("");
  const [mode, setMode] = useState<"select" | "create">("select");
  const [newName, setNewName] = useState<string>(() => extractOtherSiteName(flag.reason));

  const sites = useQuery({
    queryKey: ["sites", customerId],
    queryFn: () => console_.sites(customerId!),
    enabled: customerId !== null,
  });

  // AI 候補（site_id 付き＝既存 or label のみ＝新規名の下書き）。無ければ chips は出ない（第1区間）。
  const candidates = (candidatesOf(flag)?.options ?? []) as { site_id?: number; label?: string }[];

  const submit = () => {
    if (mode === "create") return resolve.mutate({ new_site: { name: newName.trim() } });
    return resolve.mutate({ site_id: Number(value) });
  };
  const canResolve = mode === "create" ? newName.trim().length > 0 : Boolean(value);

  return (
    <FlagShell flag={flag}>
      <ErrorNote error={resolve.error} />

      {candidates.length > 0 && (
        <div style={{ display: "flex", gap: "var(--kz-space-2)", flexWrap: "wrap", marginBottom: "var(--kz-space-2)" }}>
          {candidates.map((c, i) => (
            <button
              key={i}
              type="button"
              data-testid={`flag-${flag.id}-candidate-${i}`}
              disabled={resolve.isPending}
              onClick={() => {
                // site_id 付き候補は既存＝そのまま確定。label のみは新規名の下書きにする。
                if (c.site_id != null) resolve.mutate({ site_id: c.site_id });
                else {
                  setMode("create");
                  setNewName(c.label ?? "");
                }
              }}
              className="kz-chip"
            >
              {c.label}
            </button>
          ))}
        </div>
      )}

      {/* 切り替えは**セグメントタブ**（`.kz-tabs` の器＋`.kz-tab`）＝選択が枠の色ではなく面で読める */}
      <div style={{ display: "flex", marginBottom: "var(--kz-space-2)" }}>
        <div className="kz-tabs">
          {(["select", "create"] as const).map((m) => (
            <button
              key={m}
              type="button"
              data-testid={`flag-${flag.id}-mode-${m}`}
              aria-pressed={mode === m}
              onClick={() => setMode(m)}
              className={mode === m ? "kz-tab is-selected" : "kz-tab"}
            >
              {m === "select" ? "既存から選ぶ" : "新しい現場を登録"}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--kz-space-2)" }}>
        {mode === "select" ? (
          <select className="kz-select"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            data-testid={`flag-${flag.id}-select`}
            style={{ ...textInput }}
          >
            <option value="">現場を選ぶ</option>
            {(sites.data ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        ) : (
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            data-testid={`flag-${flag.id}-new-name`}
            placeholder="現場名（マスタに登録されます）"
            style={textInput}
          />
        )}
        <button
          type="button"
          disabled={!canResolve || resolve.isPending}
          onClick={submit}
          data-testid={`flag-${flag.id}-resolve`}
          className="kz-btn kz-btn--primary"
        >
          {mode === "create" ? "登録して確定" : "確定"}
        </button>
      </div>
    </FlagShell>
  );
}

// ---------------------------------------------------------------------------
// 希望日・配送便＝候補が無い（AI は候補を出さない）＝人が値を入れて確定
// ---------------------------------------------------------------------------

function DateFlag({ flag, detail, orderId }: { flag: ReviewFlag; detail: Detail; orderId: number }) {
  const resolve = useResolveFlag(orderId, flag.id, detail.order.customer_id);
  const [value, setValue] = useState<string>(detail.order.desired_date ?? "");
  return (
    <FlagShell flag={flag}>
      <ErrorNote error={resolve.error} />
      <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--kz-space-2)" }}>
        <input
          type="date"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          data-testid={`flag-${flag.id}-date`}
          style={textInput}
        />
        <button
          type="button"
          disabled={!value || resolve.isPending}
          onClick={() => resolve.mutate({ desired_date: value })}
          data-testid={`flag-${flag.id}-resolve`}
          className="kz-btn kz-btn--primary"
        >
          確定
        </button>
      </div>
    </FlagShell>
  );
}

function BinFlag({ flag, orderId, customerId }: { flag: ReviewFlag; orderId: number; customerId: number | null }) {
  const resolve = useResolveFlag(orderId, flag.id, customerId);
  const bins = useQuery({ queryKey: ["bins"], queryFn: () => console_.bins() });
  const [value, setValue] = useState<string>("");
  return (
    <FlagShell flag={flag}>
      <ErrorNote error={resolve.error} />
      <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--kz-space-2)" }}>
        <select className="kz-select"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          data-testid={`flag-${flag.id}-select`}
          style={textInput}
        >
          <option value="">配送便を選ぶ</option>
          {(bins.data ?? []).map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}（{b.cutoff_time} 締め）
            </option>
          ))}
        </select>
        <button
          type="button"
          disabled={!value || resolve.isPending}
          onClick={() => resolve.mutate({ bin_id: Number(value) })}
          data-testid={`flag-${flag.id}-resolve`}
          className="kz-btn kz-btn--primary"
        >
          確定
        </button>
      </div>
    </FlagShell>
  );
}

// ---------------------------------------------------------------------------
// 明細＝数量候補・単価適用
// ---------------------------------------------------------------------------

function QtyFlag({ flag, orderId, customerId }: { flag: ReviewFlag; orderId: number; customerId: number | null }) {
  const resolve = useResolveFlag(orderId, flag.id, customerId);
  const options = (candidatesOf(flag)?.options ?? []).filter((n): n is number => typeof n === "number");
  const [value, setValue] = useState<string>("");
  const submit = (n: number) => resolve.mutate({ qty: n });
  return (
    <FlagShell flag={flag}>
      <ErrorNote error={resolve.error} />
      {options.length > 0 && (
        <div style={{ display: "flex", gap: "var(--kz-space-2)", flexWrap: "wrap", marginBottom: "var(--kz-space-2)" }}>
          {options.map((n, i) => (
            <button
              key={i}
              type="button"
              data-testid={`flag-${flag.id}-candidate-${i}`}
              disabled={resolve.isPending}
              onClick={() => submit(n)}
              className="kz-chip"
            >
              <span className="kz-num">{n}</span>
            </button>
          ))}
        </div>
      )}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--kz-space-2)" }}>
        <input
          type="number"
          min="0"
          step="0.01"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          data-testid={`flag-${flag.id}-qty`}
          placeholder="数量を入力"
          style={textInput}
        />
        <button
          type="button"
          disabled={!value || Number(value) <= 0 || resolve.isPending}
          onClick={() => submit(Number(value))}
          data-testid={`flag-${flag.id}-resolve`}
          className="kz-btn kz-btn--primary"
        >
          確定
        </button>
      </div>
    </FlagShell>
  );
}

function PriceFlag({ flag, orderId, customerId }: { flag: ReviewFlag; orderId: number; customerId: number | null }) {
  const resolve = useResolveFlag(orderId, flag.id, customerId);
  const cand = candidatesOf(flag);
  const preview = cand?.preview;
  const previewLabel =
    preview != null && preview !== "" ? `${Number(preview).toLocaleString("ja-JP")}円` : "定価×掛率";
  return (
    <FlagShell flag={flag}>
      <ErrorNote error={resolve.error} />
      <button
        type="button"
        data-testid={`flag-${flag.id}-resolve`}
        disabled={resolve.isPending}
        onClick={() => resolve.mutate({})}
        className="kz-btn kz-btn--primary"
      >
        {previewLabel} を適用
      </button>
    </FlagShell>
  );
}

// ---------------------------------------------------------------------------
// 備考・AI 失敗＝表示のみ（人が原文を見て対応）＋確認して閉じる
// ---------------------------------------------------------------------------

function NoteFlag({ flag, orderId, customerId }: { flag: ReviewFlag; orderId: number; customerId: number | null }) {
  const resolve = useResolveFlag(orderId, flag.id, customerId);
  const cand = candidatesOf(flag);
  const aiFailed = cand?.type === "ai_failed";
  return (
    <FlagShell flag={flag}>
      <ErrorNote error={resolve.error} />
      <div style={{ display: "flex", alignItems: "center", gap: "var(--kz-space-2)", flexWrap: "wrap" }}>
        {aiFailed && <Badge tone="warn">読めなかった注文</Badge>}
        {cand?.text && (
          <span style={{ fontSize: "var(--kz-font-size-sm)", color: "var(--kz-color-ink-muted)" }}>
            {cand.text}
          </span>
        )}
        <button
          type="button"
          data-testid={`flag-${flag.id}-resolve`}
          disabled={resolve.isPending}
          onClick={() => resolve.mutate({})}
          className="kz-btn"
          style={{ marginLeft: "auto" }}
        >
          確認しました
        </button>
      </div>
    </FlagShell>
  );
}
